import { useCallback, useEffect, useRef, useState } from "react";
import Administration from "@/components/userAdministration/administration";
import { IDropDownOption } from "@/types/DropDown";
import { Grid } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Modal from "@/components/modal";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { api } from "@/helper/api";
import CsvImportResult, {
  CsvImportResultData,
} from "@/components/userAdministration/csvImportResult";
import useWorkerCsvImportTracking from "@/components/userAdministration/useWorkerCsvImportTracking";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import RestrictedPage from "@/consumer/restrictedPage";
import styles from "@/routes/userAdministration.module.css";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useNavigate, useParams } from "react-router-dom";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import SelectedSubjectDisplay from "@/components/general/selectedSubjectDisplay";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

export default function UserAdministration() {
  const { t } = useTranslation();
  usePageHeading(t("customer"));
  const [companySubjectId, setCompanySubjectId] = useState<string>("");
  const [companyId, setCompanyId] = useState<string>("");
  const [urlCompanyName, setUrlCompanyName] = useState<string>("");
  const [uploadSuccessful, setUploadSuccessful] = useState<boolean>(false);
  const [departmentEntries, setDepartmentEntries] =
    useState<CsvImportResultData | null>(null);
  const [departmentEntriesModal, setDepartmentEntriesModal] =
    useState<boolean>(false);
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();

  /*eslint-disable */
  const [subjectOptions, setSubjectOptions] = useState<IDropDownOption[]>([]);
  /*eslint-enable */
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const loggedInUser = useSelector((state: RootState) => state.login);

  const customerRef = useRef(null);
  const dispatch = useDispatch();
  const { showConfirmationModal, showConfirmationModalAsync } =
    useConfirmationModal();
  const navigate = useNavigate();
  const { subjectId: urlSubjectId } = useParams<{ subjectId?: string }>();

  const handleUploadSuccess = useCallback(() => {
    setUploadSuccessful((prev) => !prev);
  }, []);

  const managerImportSubjectId = String(
    selectedChildCompany?.subjectId || loggedInUser?.subjectId || "",
  );
  const { runningImport: runningWorkerCsvImport, trackImport } =
    useWorkerCsvImportTracking({
      companySubjectId:
        subdomain === "admin" ? companySubjectId : managerImportSubjectId,
      enabled: subdomain === "admin" || subdomain === "manage",
      onImportFinished: handleUploadSuccess,
    });

  useEffect(() => {
    if (subdomain !== "admin") return;
    if (!urlSubjectId) return;

    // When opened with a subjectId in the URL, we do NOT dispatch to global state.
    api.genericApiRequest({
      method: "get",
      entity: "subject",
      entityId: urlSubjectId,
      successHandler: (res: any) => {
        const subject = res.data;
        if (subject?.is_company) {
          setCompanySubjectId(subject.id);
          setCompanyId(subject.company?.id || "");
          setUrlCompanyName(subject.name || "");
          dispatch({ type: "clearTableSearch" });
          return;
        }

        // Fallback: if a user subject is provided, use its company.
        if (subject?.company?.subject_id) {
          setCompanySubjectId(subject.company.subject_id);
          setCompanyId(subject.company?.id || "");
          setUrlCompanyName(subject.company?.name || "");
          dispatch({ type: "clearTableSearch" });
          return;
        }

        setCompanySubjectId("");
        setCompanyId("");
        setUrlCompanyName("");
      },
      failHandler: () => {
        setCompanySubjectId("");
        setCompanyId("");
        setUrlCompanyName("");
      },
    });
  }, [dispatch, subdomain, urlSubjectId]);

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  const handleCompanySelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setCompanySubjectId(selectedOption.value);
      setCompanyId(selectedOption.companyId);
      dispatch({ type: "clearTableSearch" });
    }
  };

  const handleResetToSubjectSearch = () => {
    dispatch(setSubjectSearch({}));
    setCompanySubjectId("");
    setCompanyId("");
    setUrlCompanyName("");
    dispatch({ type: "clearTableSearch" });
    navigate("/admin/userAdministration");
  };

  const handleCloseDepartmentEntriesModal = () => {
    setDepartmentEntriesModal(false);
    setDepartmentEntries(null);
  };

  const handleSendRegistrationEmail = async (
    row: any,
    skipQuestion?: false | undefined,
  ) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        row.original.is_registered
          ? t("send_passwordreset_email_question", {
              email: row.original.email,
            })
          : t("send_registration_email_question", {
              email: row.original.email,
            }),
      );
      if (!isConfirmed) return;
    }

    const result = { impersonate_subject: selectedChildCompany.subjectId };
    const additionalRouteParts: any = {};
    additionalRouteParts["send_user_registration_email"] = "/";

    api.genericApiRequest({
      entity: "subject",
      method: "post",
      successHandler: () => {
        setNotificationMessage(t("successfully_sent"));
        setNotificationVariant("success");
        setShowNotification(true);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: row.original.subject_id,
      shouldNotImpersonate: true,
      additionalRouteParts: additionalRouteParts,
      submitData: result,
    });
  };

  const handleUnblockUser = async (
    row: any,
    skipQuestion?: false | undefined,
  ) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("unblock_user_question", {
          username: row.original.first_name + " " + row.original.last_name,
        }),
      );
      if (!isConfirmed) return;
    }

    api.genericApiRequest({
      entity: "user",
      entityId: row.original.id,
      method: "put",
      additionalRouteParts: { unblock_login_access: "/" },
      successHandler: () => {
        setNotificationMessage(t("user_successfully_unblocked"));
        setNotificationVariant("success");
        setShowNotification(true);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const handleUndeleteUser = async (
    row: any,
    reset: () => void,
    skipQuestion?: false | undefined,
  ) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("undelete_user_question", {
          username: row.original.first_name + " " + row.original.last_name,
        }),
      );
      if (!isConfirmed) return;
    }
    const result = { is_active: true, email: row.original.email };
    api.genericApiRequest({
      entity: "user",
      entityId: row.original.id,
      method: "put",
      successHandler: () => {
        setNotificationMessage(t("user_successfully_undeleted"));
        setNotificationVariant("success");
        setShowNotification(true);
        reset();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      submitData: result,
    });
  };

  const showWorkerCsvImportStartedModal = () => {
    showConfirmationModal(
      <div className={styles.taskStartedContainer}>
        <p className={styles.taskStartedText}>
          {t("worker_csv_import_started_infotext")}
        </p>
        <p className={styles.taskStartedHint}>
          <InfoOutlinedIcon fontSize="small" />
          {t("create_document_task_waiting_infotext")}
        </p>
      </div>,
      { title: t("task_created"), disableCancelButton: true },
    );
  };

  const handleTaskTrackingId = (taskTrackingId: string) => {
    if (!taskTrackingId) return;
    trackImport(taskTrackingId);
    showWorkerCsvImportStartedModal();
  };

  const handleDepartmentResults = (results: CsvImportResultData) => {
    setDepartmentEntries(results);
    setDepartmentEntriesModal(true);
    handleUploadSuccess();
  };

  return (
    <RestrictedPage permissionRequired="frontend_view_user_administration">
      <Grid container>
        {subdomain === "admin" && (
          <Grid item xs={12} className={styles.dropDownContainer}>
            {urlSubjectId ? (
              <SelectedSubjectDisplay
                wrapperClassName={styles.dropDownSubjectSearch}
                subjectName={urlCompanyName}
                onReset={handleResetToSubjectSearch}
              />
            ) : (
              <div className={styles.dropDownSubjectSearch}>
                <FormFieldLabel
                  label={t("company")}
                  htmlFor="user-administration-company"
                />
                <DropDownSubjectSearch
                  name="user-administration-company"
                  subdomain={subdomain}
                  options={subjectOptions}
                  onChange={handleCompanySelect}
                  queryParameter="companies"
                  autofocus={true}
                  customRef={customerRef}
                  useGlobalState={true}
                  isClearable={true}
                />
              </div>
            )}
          </Grid>
        )}
      </Grid>

      {subdomain === "admin" && companySubjectId ? (
        <Administration
          companySubjectId={companySubjectId}
          companyId={companyId}
          uploadSuccessful={uploadSuccessful}
          handleSendRegistrationEmail={handleSendRegistrationEmail}
          handleUnblockUser={handleUnblockUser}
          handleUndeleteUser={handleUndeleteUser}
          subdomain={subdomain}
          handleUploadSuccess={handleUploadSuccess}
          selectedChildCompany={selectedChildCompany}
          handleTaskTrackingId={handleTaskTrackingId}
          handleDepartmentResults={handleDepartmentResults}
          isEntriesModalOpen={departmentEntriesModal}
          runningWorkerCsvImport={runningWorkerCsvImport}
        />
      ) : subdomain === "manage" ? (
        <Administration
          uploadSuccessful={uploadSuccessful}
          selectedChildCompany={selectedChildCompany}
          handleUploadSuccess={handleUploadSuccess}
          subdomain={subdomain}
          handleSendRegistrationEmail={handleSendRegistrationEmail}
          handleTaskTrackingId={handleTaskTrackingId}
          isEntriesModalOpen={departmentEntriesModal}
          runningWorkerCsvImport={runningWorkerCsvImport}
        />
      ) : null}
      <Modal
        open={departmentEntriesModal}
        hideCancelButton={true}
        onSubmit={handleCloseDepartmentEntriesModal}
        submitButtonText={t("confirm")}
        buttonPlacement="flex-end"
        title={t("overview")}
      >
        <CsvImportResult
          result={departmentEntries}
          createdLabel={t("created_departments_entries")}
          updatedLabel={t("updated_departments_entries")}
        />
      </Modal>
      {errorModal}
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </RestrictedPage>
  );
}
